import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
    FaTimes, FaPaperPlane, FaInfoCircle, FaLaptopCode, FaGlobeAfrica, 
    FaBalanceScale, FaGraduationCap, FaQuestionCircle, FaChartLine, FaPalette
} from 'react-icons/fa';
import styles from './publish.module.css';
import { API_URL } from '../Utils/api';

const CATEGORIES = [
    { id: 'tech', label: 'Technologie', icon: FaLaptopCode, color: '#3b82f6' },
    { id: 'society', label: 'Société', icon: FaGlobeAfrica, color: '#ef4444' },
    { id: 'edu', label: 'Éducation', icon: FaGraduationCap, color: '#8b5cf6' },
    { id: 'eco', label: 'Économie', icon: FaChartLine, color: '#10b981' },
    { id: 'culture', label: 'Culture', icon: FaPalette, color: '#f59e0b' },
    { id: 'other', label: 'Autre', icon: FaQuestionCircle, color: '#6b7280' },
];

const TAGS_LIST = [
    'Intelligence Artificielle', 'Environnement', 'Politique', 'Santé', 
    'Entrepreneuriat', 'Logement', 'Transport', 'Crypto', 
    'Éthique', 'Future of Work', 'Inclusion', 'Innovation'
];

const MAX_QUESTION_CHARS = 140;
const MAX_DETAILS_CHARS = 1000;

const AskQuestion = ({ back }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        question: '',
        details: '',
        category: '',
        tags: []
    });
    const [isLoading, setIsLoading] = useState(false);
    const [toast, setToast] = useState(null);

    const isFormValid = useMemo(() => (
        formData.question.trim().length >= 10 && formData.category !== ''
    ), [formData.question, formData.category]);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    }, []);



    const toggleTag = useCallback((tag) => {
        setFormData(prev => {
            const isSelected = prev.tags.includes(tag);
            if (isSelected) {
                return { ...prev, tags: prev.tags.filter(t => t !== tag) };
            } else if (prev.tags.length < 5) {
                return { ...prev, tags: [...prev.tags, tag] };
            }
            setToast('Maximum 5 tags autorisés');
            return prev;
        });
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isFormValid) return;

        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const payload = {
                postType: 'debate',
                title: formData.question.trim(),
                postText: formData.details.trim(),
                category: formData.category,
                tags: formData.tags
            };

            await axios.post(
                `${API_URL}/publication`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } } 
            );

            setToast('Débat lancé avec succès !');
            setTimeout(() => { if (back) back(); else navigate(-1); }, 1500);
        } catch (error) {
            setToast(error.response?.data?.message || 'Erreur lors de la publication');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        const hasContent = formData.question.trim().length > 0;
        if (hasContent) {
            if (window.confirm('Abandonner la création du débat ?')) {
                if (back) back(); else navigate(-1);
            }
        } else {
            if (back) back(); else navigate(-1);
        }
    };

    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => setToast(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [toast]);

    return (
        <div className={styles.container}>
            {toast && <div className={styles.toast}>{toast}</div>}

            <header className={styles.header}>
                <button type="button" onClick={handleCancel} className={styles.closeBtn} aria-label="Fermer">
                    <FaTimes />
                </button>
                <h1 className={styles.title}>Lancer un débat</h1>
                <div className={styles.placeholder}></div>
            </header>

            <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.scrollContent}>
                    <div className={styles.infoBanner}>
                        <FaInfoCircle />
                        <span>Posez une question ouverte pour susciter la discussion.</span>
                    </div>

                    <div className={styles.section}>
                        <label className={styles.label}>Titre du débat *</label>
                        <textarea
                            name="question"
                            className={`${styles.input} ${styles.questionInput}`}
                            placeholder="Ex: Le télétravail va-t-il tuer l'esprit d'entreprise ?"
                            value={formData.question}
                            onChange={handleChange}
                            maxLength={MAX_QUESTION_CHARS}
                            rows={3}
                            required
                        />
                        <span className={styles.charCount}>
                            {formData.question.length}/{MAX_QUESTION_CHARS}
                        </span>
                    </div>



                    <div className={styles.section}>
                        <label className={styles.label}>Lancer le contexte (optionnel)</label>
                        <textarea
                            name="details"
                            className={`${styles.input} ${styles.textarea}`}
                            placeholder="Développez votre pensée..."
                            value={formData.details}
                            onChange={handleChange}
                            maxLength={MAX_DETAILS_CHARS}
                            rows={5}
                        />
                        <span className={styles.charCount}>
                            {formData.details.length}/{MAX_DETAILS_CHARS}
                        </span>
                    </div>

                    <div className={styles.section}>
                        <label className={styles.label}>Thématique *</label>
                        <div className={styles.categoriesGrid}>
                            {CATEGORIES.map((cat) => (
                                <button
                                    key={cat.id}
                                    type="button"
                                    className={`${styles.categoryCard} ${formData.category === cat.id ? styles.categoryCardActive : ''}`}
                                    onClick={() => setFormData(p => ({ ...p, category: cat.id }))}
                                >
                                    <div 
                                        className={styles.categoryIcon}
                                        style={{ 
                                            backgroundColor: formData.category === cat.id ? cat.color : 'var(--bg-hover)',
                                            color: formData.category === cat.id ? '#fff' : cat.color
                                        }}
                                    >
                                        <cat.icon />
                                    </div>
                                    <span className={styles.categoryLabel}>{cat.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={styles.section}>
                        <div className={styles.labelRow}>
                            <label className={styles.label}>Mots-clés</label>
                            <span className={styles.labelHint}>{formData.tags.length}/5</span>
                        </div>
                        <div className={styles.tagsContainer}>
                            {TAGS_LIST.map((tag) => (
                                <button
                                    key={tag}
                                    type="button"
                                    className={`${styles.tagChip} ${formData.tags.includes(tag) ? styles.tagChipActive : ''}`}
                                    onClick={() => toggleTag(tag)}
                                >
                                    {tag}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <footer className={styles.footer}>
                    <button
                        type="submit"
                        className={`${styles.submitBtn} ${(!isFormValid || isLoading) ? styles.submitBtnDisabled : ''}`}
                        disabled={!isFormValid || isLoading}
                    >
                        {isLoading ? <div className={styles.spinner} /> : (
                            <>
                                <span>Lancer le débat</span>
                            </>
                        )}
                    </button>
                </footer>
            </form>
        </div>
    );
};

export default AskQuestion;